import {LoadingOutlined} from "@ant-design/icons";
import Collapse from "@kunukn/react-collapse";
import {Breadcrumb, Button, Descriptions, Form, Input, message, Modal, Select, Skeleton, Spin} from "antd";
import {useForm, useWatch} from "antd/es/form/Form";
import React, {useContext, useEffect, useState} from "react";
import {Link, useNavigate, useParams} from "react-router-dom";
import {AppContextContext, ItemUpdateProfileServiceContext, OrganizationServiceContext, VulSourceServiceContext} from "../../Contexts";
import {Permission} from "../../domain/auth/Permission";
import Organization from "../../domain/Organization";
import {ResourceOwnerType} from "../../domain/ResourceOwnerType";
import ItemUpdateProfile from "../../domain/ItemUpdateProfile";
import VulSource from "../../domain/VulSource";
import QueryOptions from "../../sal-ui/QueryOptions";
import {ServerConstraintViolationsHolder} from "../../sal-ui/ServerConstraintViolations";
import * as globalStyles from "../App.module.css";
import {DocumentTitle} from "../DocumentTitle";
import ReadOnlyQueryView from "../vulview/ReadOnlyQueryView";
import ItemUpdateProfileQueryBuilder from "./ItemUpdateProfileQueryBuilder";
import * as styles from "./ItemUpdateProfileDetail.module.css";
import Tip from "../common/Tip";

const serverViolationsHolder = new ServerConstraintViolationsHolder();

function ItemUpdateProfileDetail() {
    const appContext = useContext(AppContextContext);
    const organizationService = useContext(OrganizationServiceContext);
    const profileService = useContext(ItemUpdateProfileServiceContext);
    const vulSourceService = useContext(VulSourceServiceContext);
    const navigate = useNavigate();
    const {profileId, cloneProfileId} = useParams<{ profileId?: string; cloneProfileId?: string }>();
    const [profile, setProfile] = useState<ItemUpdateProfile>();
    const [editMode, setEditMode] = useState(false);
    const [form] = useForm();
    const owner = useWatch("owner", form);
    const [organizations, setOrganizations] = useState<Organization[]>();
    const [sources, setSources] = useState<VulSource[]>();
    const hasManage = appContext.user.hasPermission(Permission.ITEM_UPDATE_PROFILE__MANAGE_ORG);
    const canManageProfile = hasManage && (!profileId || appContext.user.isSystemAdmin() || profile?.owner === ResourceOwnerType.ORGANIZATION);
    const sourceProfileId = profileId || cloneProfileId;
    const isClone = cloneProfileId !== undefined;
    const title = isClone
        ? <>Clone of item update profile <b>{profile?.name}</b></>
        : profileId ? profile?.name || "Item update profile" : "New item update profile";

    const layout = {
        labelCol: {span: 4},
        wrapperCol: {span: 20},
    };

    const tailLayout = {
        wrapperCol: {offset: 4, span: 20},
    };

    useEffect(() => {
        vulSourceService.getList(QueryOptions.newUnlimitedOrderedInstance("name")).then(value => setSources(value.data));

        if (appContext.user.isSystemAdmin()) {
            organizationService.getList(QueryOptions.newUnlimitedOrderedInstance("name")).then(value => setOrganizations(value.data));
        }

        if (sourceProfileId) {
            setEditMode(isClone);
            reload();
        } else {
            form.setFieldsValue({owner: ResourceOwnerType.SYSTEM});
            setEditMode(true);
        }
    }, [profileId, cloneProfileId]);

    if (sourceProfileId && !profile) {
        return <Skeleton/>;
    }

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb
                    className={globalStyles["common__breadcrumb"]}
                    items={[
                        {title: appContext.config?.appName},
                        {title: <Link to="/definitions/item-update-profiles">Item update profiles</Link>},
                        {title}
                    ]}
                />

                <Spin
                    spinning={profileId ? !profile : false}
                    indicator={<LoadingOutlined style={{fontSize: 24}} spin={true}/>}
                >
                    <h1>{title}</h1>

                    {profileId && profile && hasManage &&
                        <div className={globalStyles["common__top-button-bar"]}>
                            {canManageProfile && <>
                                <Button onClick={() => setEditMode(!editMode)}>
                                    Edit
                                </Button>
                                <Button
                                    danger
                                    onClick={() => {
                                        Modal.confirm({
                                            content: "Do you really want to delete this item update profile?",
                                            okText: "Delete",
                                            cancelText: "Cancel",
                                            okButtonProps: {danger: true},
                                            onOk: () => onDelete()
                                        });
                                    }}>
                                    Delete
                                </Button>
                            </>}

                            <Button onClick={() => navigate(`/definitions/item-update-profiles/clone/${profile.id}`)}>
                                Clone
                            </Button>
                        </div>
                    }

                    <Collapse isOpen={editMode || !profileId}>
                        {profileId && <h3>Edit item update profile</h3>}

                        <Tip
                            id="ItemUpdateProfileDetail.rules"
                            style={{marginBottom: 12, maxWidth: 900}}
                            message="Rules match individual attribute changes."
                            description="The whole profile query is evaluated against one changed attribute at a time. Attribute, change type, and numeric difference conditions always refer to that same attribute change; item and source conditions refer to the item and source of that change."
                        />

                        <Form
                            {...layout}
                            form={form}
                            onFinish={onFinish}
                            className={`${globalStyles["common__form"]} ${profileId ? globalStyles["common_form--edit"] : globalStyles["common_form--add"]} ${styles.form}`}
                        >
                            <Form.Item name="name" label="Name" rules={[{required: true, message: "Name is required."}]}>
                                <Input autoFocus={!profileId} maxLength={100}/>
                            </Form.Item>

                            <Form.Item name="description" label="Description">
                                <Input.TextArea maxLength={1000} autoSize={{minRows: 4, maxRows: 8}}/>
                            </Form.Item>

                            {appContext.user.isSystemAdmin() && <>
                                <Form.Item
                                    name="owner"
                                    label="Owner"
                                    rules={[{required: true, message: "Owner is required."}]}
                                >
                                    <Select options={[{
                                        value: ResourceOwnerType.SYSTEM,
                                        label: "system"
                                    }, {
                                        value: ResourceOwnerType.ORGANIZATION,
                                        label: "organization"
                                    }]}/>
                                </Form.Item>

                                {owner === ResourceOwnerType.ORGANIZATION && <Form.Item
                                    name="organization"
                                    label="Organization"
                                    rules={[{required: true, message: "Organization is required."}]}>
                                    <Select
                                        allowClear={true}
                                        options={organizations?.map(organization => ({value: organization.id, label: organization.name}))}
                                    />
                                </Form.Item>}
                            </>}

                            <Form.Item
                                name="query"
                                label="Rules"
                                extra="Whitelist rules. An updated item is reported when its attribute change matches this query. Numeric difference is signed: added values are positive and removed values are negative."
                                rules={[{required: true, message: "Rules are required."}]}
                            >
                                <ItemUpdateProfileQueryBuilder initialQuery={profile?.query} form={form}/>
                            </Form.Item>

                            <Form.Item {...tailLayout} className={globalStyles["common__form-buttons"]}>
                                <Button type="primary" htmlType="submit">Save</Button>
                                <Button onClick={onCancel}>Cancel</Button>
                            </Form.Item>
                        </Form>
                    </Collapse>

                    {profileId && profile &&
                        <Descriptions
                            column={1}
                            bordered={true}
                            size="small"
                            className={`${globalStyles.details} ${styles.details}`}
                            title="Profile properties"
                        >
                            {profile.owner === ResourceOwnerType.ORGANIZATION &&
                                <Descriptions.Item label="Owner">{profile.organization?.name}</Descriptions.Item>}
                            {profile.owner === ResourceOwnerType.SYSTEM &&
                                <Descriptions.Item label="Owner"><i>System profile</i></Descriptions.Item>}
                            <Descriptions.Item label="Description">
                                {profile.description || <i>no description</i>}
                            </Descriptions.Item>
                            <Descriptions.Item label="Rules">
                                <ReadOnlyQueryView sources={sources} query={JSON.parse(profile.query)}/>
                            </Descriptions.Item>
                        </Descriptions>
                    }
                </Spin>
            </>
        </DocumentTitle>
    );

    function reload() {
        if (!sourceProfileId) {
            return;
        }

        profileService.get(sourceProfileId).then(value => {
            setProfile(value);

            form.setFieldsValue({
                ...value,
                name: isClone ? `Clone of ${value.name}` : value.name,
                organization: value.organization?.id
            });
        });
    }

    function onFinish(values: any) {
        if (profileId) {
            profileService.update(profileId, values)
                .then(
                    () => {
                        message.success("Item update profile updated.");

                        setEditMode(false);

                        reload();
                    },
                    error => serverViolationsHolder.handleServerError(error, form)
                );
        } else {
            profileService.add(values)
                .then(
                    id => {
                        message.success("Item update profile added.");

                        navigate(`/definitions/item-update-profiles/${id}`);
                    },
                    error => serverViolationsHolder.handleServerError(error, form)
                );
        }
    }

    function onCancel() {
        if (!profileId) {
            navigate("/definitions/item-update-profiles");

            return;
        }

        setEditMode(false);

        if (profile) {
            form.setFieldsValue({
                ...profile,
                organization: profile.organization?.id
            });
        }
    }

    function onDelete() {
        if (!profile) {
            return;
        }

        profileService.delete(profile).then(() => {
            message.success("Item update profile deleted.");

            navigate("/definitions/item-update-profiles");
        });
    }
}

export default ItemUpdateProfileDetail;
